// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Typography,
  type ListItemProps,
} from '@mui/material';
import { DocumentScope } from '@shared/data-access/types/engine-types';
import { AppIcon, ShowInAppClient, Table } from '@shared/ui-kit';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { classNames } from '@shared/ui-kit/utils/classnames';
import type { PropsWithChildren, ReactNode } from 'react';
import { DocumentFileListItem } from '../document-file-list-item/document-file-list-item';
import {
  useDocumentsDetailDrawer,
  type UseDocumentsDetailDrawerProps,
} from './use-documents-detail-drawer';
import styles from './documents-detail-drawer.module.css';

export type DocumentsDetailDrawerProps = UseDocumentsDetailDrawerProps;

const DetailField = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
  <>
    <dt className={styles['info-term']}>
      <span className={styles['info-label']}>{label}</span>
    </dt>
    <dd className={styles['info-value']}>{value}</dd>
  </>
);

export const DocumentsDetailDrawer = defineComponent({
  displayName: 'DocumentsDetailDrawer',
  useSetup: useDocumentsDetailDrawer,

  Slots: {
    SectionTitle: ({ children }: PropsWithChildren) => (
      <Typography variant="subtitle2" className={styles['section-title']}>
        {children}
      </Typography>
    ),
    Section: ({
      divider,
      className,
      ...props
    }: ListItemProps & { divider?: boolean }) => (
      <>
        <ListItem
          component="div"
          className={classNames(styles.section, className)}
          disablePadding
          disableGutters
          {...props}
        >
          {props.children}
        </ListItem>
        {divider ? <Divider /> : null}
      </>
    ),
  },

  View: ({
    DrawerRenderer,
    onClose,
    Slots,
    t,
    document,
    detailFiles,
    isFilesLoading,
    filesError,
    scopeLabel,
    scopeChipColor,
    onDownloadAllFiles,
    onRetryLoadFiles,
    onDelete,
    isDownloadDisabled,
    isDownloadingAll,
    categoryLabels,
    subcategoryLabels,
  }) => {
    if (!document) {
      return null;
    }

    const dateFormatOptions = {
      day: 'numeric' as const,
      month: 'short' as const,
      year: 'numeric' as const,
    };

    return (
      <DrawerRenderer
        anchor="right"
        PaperProps={{
          classes: {
            root: styles['drawer-paper'],
          },
        }}
        data-testid="documents-detail-drawer"
      >
        <List
          dense
          disablePadding
          component="div"
          className={styles['drawer-list']}
        >
          <Slots.Section
            className={classNames(
              styles['section-sticky-top'],
              styles['section-header'],
            )}
            divider
          >
            <header className={styles['document-header']}>
              <figure className={styles['document-icon-wrap']}>
                <AppIcon icon="FileText" color="inherit" size="lg" />
              </figure>

              <div className={styles['document-header-content']}>
                <Typography
                  variant="h6"
                  component="h2"
                  className={styles['document-title']}
                >
                  {document.title}
                </Typography>

                <div className={styles['document-chips']}>
                  <Chip
                    label={scopeLabel}
                    size="small"
                    variant="outlined"
                    color={scopeChipColor[document.scope]}
                    className="!max-h-6"
                    data-testid="documents-detail-scope-chip"
                  />
                </div>
              </div>
            </header>

            <div className={styles['header-close-wrap']}>
              <IconButton
                icon="Close"
                variant="icon"
                size="md"
                color="main"
                aria-label={t('common.close', { ns: 'common' })}
                className={styles['close-button']}
                onClick={onClose}
                data-testid="documents-detail-drawer-close"
              />
            </div>
          </Slots.Section>

          <Slots.Section divider>
            <div className={styles['section-body']}>
              <Slots.SectionTitle>
                <Translation
                  i18nKey="documents.detail-drawer.sections.files"
                  ns="components"
                />
              </Slots.SectionTitle>

              {isFilesLoading ? (
                <Typography variant="body2" className="!text-primary-medium">
                  <Translation
                    i18nKey="documents.detail-drawer.files-loading"
                    ns="components"
                  />
                </Typography>
              ) : null}

              {filesError ? (
                <div className="flex flex-col gap-2">
                  <Typography variant="body2" className="!text-error-main">
                    <Translation
                      i18nKey="documents.detail-drawer.files-error"
                      ns="components"
                    />
                  </Typography>
                  <Button
                    type="button"
                    variant="outlined"
                    color="secondary"
                    onClick={() => void onRetryLoadFiles()}
                    className="!w-fit normal-case"
                    data-testid="documents-detail-files-retry"
                  >
                    <Translation
                      i18nKey="documents.detail-drawer.files-retry"
                      ns="components"
                    />
                  </Button>
                </div>
              ) : null}

              {!isFilesLoading && !filesError && detailFiles.length > 0 ? (
                <ul
                  className="flex flex-col gap-3"
                  data-testid="documents-detail-files-list"
                >
                  {detailFiles.map(file => (
                    <DocumentFileListItem
                      key={`${file.file_name}-${file.file_size}`}
                      fileName={file.file_name}
                      fileSize={file.file_size}
                      surface="section"
                      testId={`documents-detail-file-${file.file_name}`}
                      downloadUrl={file.url}
                      downloadAriaLabel={t('documents.row-actions.download', {
                        ns: 'components',
                      })}
                      downloadTestId={`documents-detail-file-download-${file.file_name}`}
                    />
                  ))}
                </ul>
              ) : null}

              {!isFilesLoading && !filesError && detailFiles.length === 0 ? (
                <Typography variant="body2" className="!text-primary-medium">
                  <Translation
                    i18nKey="documents.detail-drawer.files-empty"
                    ns="components"
                  />
                </Typography>
              ) : null}
            </div>
          </Slots.Section>

          <Slots.Section divider>
            <div className={styles['section-body']}>
              <Slots.SectionTitle>
                <Translation
                  i18nKey="documents.detail-drawer.sections.metadata"
                  ns="components"
                />
              </Slots.SectionTitle>

              <dl className={styles['info-grid']}>
                <DetailField
                  label={
                    <Translation
                      i18nKey="documents.detail-drawer.fields.category"
                      ns="components"
                    />
                  }
                  value={categoryLabels[document.category] ?? document.category}
                />
                <DetailField
                  label={
                    <Translation
                      i18nKey="documents.detail-drawer.fields.subcategory"
                      ns="components"
                    />
                  }
                  value={
                    subcategoryLabels[document.subcategory] ?? document.subcategory
                  }
                />
                <DetailField
                  label={
                    <Translation
                      i18nKey="documents.detail-drawer.fields.issuer"
                      ns="components"
                    />
                  }
                  value={document.issuer ?? '-'}
                />
                <DetailField
                  label={
                    <Translation
                      i18nKey="documents.detail-drawer.fields.document-date"
                      ns="components"
                    />
                  }
                  value={
                    <Table.Data
                      format="date"
                      value={document.document_date}
                      safeValue="—"
                      formatOptions={dateFormatOptions}
                      container={false}
                    />
                  }
                />
                <DetailField
                  label={
                    <Translation
                      i18nKey="documents.detail-drawer.fields.expiry-date"
                      ns="components"
                    />
                  }
                  value={
                    <Table.Data
                      format="date"
                      value={document.expiry_date}
                      safeValue="—"
                      formatOptions={dateFormatOptions}
                      container={false}
                    />
                  }
                />
                <DetailField
                  label={
                    <Translation
                      i18nKey="documents.detail-drawer.fields.amount"
                      ns="components"
                    />
                  }
                  value={
                    document.amount ? (
                      <Table.Data
                        format="currency"
                        value={document.amount}
                        currency={document.currency ?? 'EUR'}
                        container={false}
                      />
                    ) : (
                      '-'
                    )
                  }
                />
              </dl>
            </div>
          </Slots.Section>

          <Slots.Section>
            <div className={styles['section-body']}>
              <Slots.SectionTitle>
                <Translation
                  i18nKey="documents.detail-drawer.sections.audit"
                  ns="components"
                />
              </Slots.SectionTitle>

              <dl className={styles['info-grid']}>
                <DetailField
                  label={
                    <Translation
                      i18nKey="documents.detail-drawer.fields.uploaded-at"
                      ns="components"
                    />
                  }
                  value={
                    <Table.Data
                      format="date"
                      value={document.uploaded_at}
                      formatOptions={dateFormatOptions}
                      container={false}
                    />
                  }
                />
                <DetailField
                  label={
                    <Translation
                      i18nKey="documents.detail-drawer.fields.updated-at"
                      ns="components"
                    />
                  }
                  value={
                    <Table.Data
                      format="date"
                      value={document.updated_at}
                      formatOptions={dateFormatOptions}
                      container={false}
                    />
                  }
                />
              </dl>
            </div>
          </Slots.Section>

          <Slots.Section className={styles['section-spacer']} />

          <Slots.Section className={styles['section-sticky-bottom']}>
            <div className={styles['footer-actions']}>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                fullWidth
                onClick={onDownloadAllFiles}
                disabled={isDownloadDisabled}
                startIcon={
                  isDownloadingAll ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AppIcon icon="Download" color="inherit" size="lg" />
                  )
                }
                data-testid="documents-detail-download"
              >
                <Translation
                  i18nKey="documents.detail-drawer.download-all"
                  ns="components"
                />
              </Button>

              {document.scope !== DocumentScope.SilverAlpha ? (
                <ShowInAppClient>
                  <Button
                    type="button"
                    variant="text"
                    color="error"
                    fullWidth
                    onClick={onDelete}
                    startIcon={
                      <AppIcon icon="Trashcan" color="inherit" size="lg" />
                    }
                    data-testid="documents-detail-delete"
                  >
                    <Translation
                      i18nKey="documents.detail-drawer.delete"
                      ns="components"
                    />
                  </Button>
                </ShowInAppClient>
              ) : null}
            </div>
          </Slots.Section>
        </List>
      </DrawerRenderer>
    );
  },
});
